import * as React from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { FC, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { currencyString } from '@sales-center/common'
import { ISalesFunnelCard } from '@user/store/salesFunnelSummary/typings'

import { defaultFilters as defaultLeadFilters } from '@user/store/lead/filters'
import { defaultFilters as defaultOppFilters } from '@user/store/opportunity/filters'
import { ENTITY_TYPES } from '@sales-center/common'
import { selectSalesFunnelFilters } from '@user/store/salesFunnelSummary/slice'
import { ToTitleCase } from '@user/utils/ToTitleCase'
import { useSaveLeadFilters } from '@user/store/lead/hooks'
import { useSaveOpportunityFilters } from '@user/store/opportunity'
import { useNavigate } from 'react-router'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

const getStyles = (theme: Theme) => ({
  status: {
    marginBottom: theme.spacing(0.5),
  },

  metric: {
    marginRight: theme.spacing(2),
  },

  title: {
    fontFamily: 'Source Sans Pro Semibold',
    fontSize: '16px',
    marginBottom: theme.spacing(2),
  },
})

interface Props {
  entityType: ENTITY_TYPES
  funnelCard: ISalesFunnelCard
  handleClose: () => void
}

export const SalesFunnelStageStats: FC<Props> = (props) => {
  const {
    entityType,
    funnelCard,
    funnelCard: { salesFunnelId, salesFunnelName },
    handleClose,
  } = props

  const stats = useMemo(() => {
    return entityType == ENTITY_TYPES.Lead
      ? funnelCard.stageDetails.leads
      : funnelCard.stageDetails.opportunities
  }, [entityType, funnelCard])

  const funnelFilters = useSelector(selectSalesFunnelFilters)
  const navigate = useNavigate()
  const saveLeadFilters = useSaveLeadFilters()
  const saveOpportunityFilters = useSaveOpportunityFilters()

  const goToLeadStatus = useCallback(
    (statusId: number, statusName: string) => {
      saveLeadFilters({
        ...defaultLeadFilters,
        filters: {
          ...defaultLeadFilters.filters,
          closed: {
            ...defaultLeadFilters.filters.closed,
            value: 'empty',
          },
          createdDate: {
            ...defaultLeadFilters.filters.createdDate,
            value: funnelFilters.filters.createdDate.value,
          },
          salesFunnelId: {
            ...defaultLeadFilters.filters.salesFunnelId,
            value:
              salesFunnelId && salesFunnelName
                ? {
                    mode: 'any',
                    options: [{ label: salesFunnelName, value: salesFunnelId }],
                  }
                : defaultLeadFilters.filters.salesFunnelId.value,
          },
          assignee: {
            ...defaultLeadFilters.filters.assignee,
            value: funnelFilters.filters.ownerIds.value,
          },
          salesFunnelStageId: {
            ...defaultLeadFilters.filters.salesFunnelStageId,
            value:
              statusId && statusName
                ? {
                    mode: 'any',
                    options: [{ label: statusName, value: statusId }],
                  }
                : defaultLeadFilters.filters.salesFunnelStageId.value,
          },
          salesTeamId: {
            ...defaultLeadFilters.filters.salesTeamId,
            value: funnelFilters.filters.salesTeamIds.value,
          },
        },
      })

      navigate('/leads')
      handleClose()
    },
    [
      funnelFilters,
      handleClose,
      navigate,
      saveLeadFilters,
      salesFunnelId,
      salesFunnelName,
    ],
  )

  const goToOppStatus = useCallback(
    (stageId: number, stageName: string) => {
      saveOpportunityFilters({
        ...defaultOppFilters,
        filters: {
          ...defaultOppFilters.filters,
          closed: {
            ...defaultOppFilters.filters.closed,
            value: 'empty',
          },
          createdDate: {
            ...defaultOppFilters.filters.createdDate,
            value: funnelFilters.filters.createdDate.value,
          },
          salesFunnelId: {
            ...defaultOppFilters.filters.salesFunnelId,
            value:
              salesFunnelId && salesFunnelName
                ? {
                    mode: 'any',
                    options: [{ label: salesFunnelName, value: salesFunnelId }],
                  }
                : defaultOppFilters.filters.salesFunnelId.value,
          },
          assignee: {
            ...defaultOppFilters.filters.assignee,
            value: funnelFilters.filters.ownerIds.value,
          },
          salesFunnelStageId: {
            ...defaultOppFilters.filters.salesFunnelStageId,
            value:
              stageId && stageName
                ? {
                    mode: 'any',
                    options: [{ label: stageName, value: stageId }],
                  }
                : defaultOppFilters.filters.salesFunnelStageId.value,
          },
          salesTeamId: {
            ...defaultOppFilters.filters.salesTeamId,
            value: funnelFilters.filters.salesTeamIds.value,
          },
        },
      })

      navigate('/opportunities')
      handleClose()
    },
    [
      funnelFilters,
      handleClose,
      navigate,
      saveOpportunityFilters,
      salesFunnelId,
      salesFunnelName,
    ],
  )

  const handleClick = useCallback(
    (stageId: number, stageName: string) => (_e: React.ChangeEvent<unknown>) =>
      entityType == ENTITY_TYPES.Lead
        ? goToLeadStatus(stageId, stageName)
        : goToOppStatus(stageId, stageName),
    [entityType, goToLeadStatus, goToOppStatus],
  )

  const title = useMemo(() => {
    return `${ToTitleCase(entityType)} ${
      entityType == ENTITY_TYPES.Lead ? 'Statuses' : 'Stages'
    }`
  }, [entityType])
  const styles = getStyles(useTheme())
  return (
    <Box>
      <Typography sx={styles.title}>{title}</Typography>
      <Grid container direction="column" spacing={2}>
        {!!stats &&
          stats.length &&
          stats.map((s, i) => (
            <Grid item key={i}>
              <Box display="flex" alignItems="center">
                <Box display="flex" flexGrow={1} flexDirection="column">
                  <Typography sx={styles.status}>
                    {entityType == ENTITY_TYPES.Lead ? 'Status' : 'Stage'}:{' '}
                    <b>{s.stageName}</b>
                  </Typography>
                  <Box display="flex">
                    <Typography sx={styles.metric}>
                      Value: <b>{`$${currencyString(s.stageValue)}`}</b>
                    </Typography>
                    <Typography sx={styles.metric}>
                      Count: <b>{s.stageCount}</b>
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Button
                    onClick={handleClick(s.salesFunnelStageId, s.stageName)}
                    variant="outlined"
                    data-test-id={
                      entityType == ENTITY_TYPES.Lead
                        ? 'viewLeadsBtn'
                        : 'viewOpportunitiesBtn'
                    }
                  >
                    View{' '}
                    {entityType == ENTITY_TYPES.Lead
                      ? 'Leads'
                      : 'Opportunities'}
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
      </Grid>
    </Box>
  )
}

SalesFunnelStageStats.displayName = 'SalesFunnelStageStats'
